import type React from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { AriaExtendedText } from '@xing-com/crate-jobs-components-aria-extended-text';
import { MultiLocationDisplay } from '@xing-com/crate-jobs-components-multi-location-display';
import type { CompanyData, JobDetails } from '@xing-com/crate-jobs-helpers';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { useExperiment } from '@xing-com/hub';
import { IconMarketingSolutionsMegaphone } from '@xing-com/icons';
import { NotOnSmall, OnlyOnSmall } from '@xing-com/layout-primitives';
import { mediaConfined } from '@xing-com/layout-tokens';
import { Marker } from '@xing-com/marker';
import { spaceL, spaceM, spaceXL, spaceXS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { CompanyImage, CompanyName } from '../../company-info';
import { Title } from '../title';

const AdditionalInfos = styled.div`
  display: flex;
  gap: ${spaceXS};
  flex-direction: column;
`;

const AdditionalInfo = styled(BodyCopy)`
  ${lineClamp(1)}
`;

const CompanyInfo = styled.div`
  display: flex;
  gap: ${spaceM};
  margin-bottom: ${spaceXL};

  @media ${mediaConfined} {
    margin-top: ${spaceL};
  }
`;

const SmallTitleContainer = styled(OnlyOnSmall)`
  margin-bottom: ${spaceXS};
`;

const Collapsible = styled.button`
  all: unset;
  cursor: pointer;
  position: relative;
`;

const ReachRefreshMarker = styled(Marker)`
  width: fit-content;
`;

type Props = {
  title?: string;
  company?: CompanyData;
  industry?: string;
  locations?: JobDetails['locations'];
  isReachRefreshed?: boolean;
};

export const JobIntro: React.FC<Props> = ({
  title,
  company,
  industry,
  locations,
  isReachRefreshed = false,
}) => {
  const [areLocationsCollapsed, setAreLocationsCollapsed] = useState(true);

  const reachRefreshVariant = useExperiment('ABACUS-539');
  const hasReachRefreshMarker =
    isReachRefreshed &&
    (reachRefreshVariant === 'B' || reachRefreshVariant === 'C');
  const reachRefreshMarkerCopyId =
    reachRefreshVariant === 'B'
      ? 'JOBS_DETAIL_STILL_AVAILABLE_TAG'
      : 'JOBS_DETAIL_REACTIVATED_TAG';

  if (!company) {
    return null;
  }

  return (
    <>
      <NotOnSmall>
        <Title title={title} />
      </NotOnSmall>
      <CompanyInfo>
        <CompanyImage logo={company.logo} link={company.link} />
        <AdditionalInfos>
          <SmallTitleContainer>
            <Title title={title} />
          </SmallTitleContainer>
          {hasReachRefreshMarker ? (
            <ReachRefreshMarker icon={IconMarketingSolutionsMegaphone}>
              <AriaExtendedText
                item={{ id: reachRefreshMarkerCopyId }}
                a11yItem={{ id: `${reachRefreshMarkerCopyId}_A11Y` }}
                noWrapper
              />
            </ReachRefreshMarker>
          ) : null}
          <CompanyName name={company.name} link={company.link} />
          <NotOnSmall>
            <AdditionalInfo noMargin>{industry}</AdditionalInfo>
          </NotOnSmall>
          <NotOnSmall>
            <BodyCopy noMargin>
              {locations?.map((location) => location.city).join(', ')}
            </BodyCopy>
          </NotOnSmall>
          <OnlyOnSmall>
            {areLocationsCollapsed ? (
              <Collapsible onClick={() => setAreLocationsCollapsed(false)}>
                <MultiLocationDisplay lines={2} locations={locations ?? []} />
              </Collapsible>
            ) : (
              <Collapsible onClick={() => setAreLocationsCollapsed(true)}>
                <BodyCopy noMargin>
                  {locations?.map((location) => location.city).join(', ')}
                  <br />
                  <b>
                    <FormattedMessage id="JOBS_MULTILOCATION_SHOW_LESS_LABEL" />
                  </b>
                </BodyCopy>
              </Collapsible>
            )}
          </OnlyOnSmall>
        </AdditionalInfos>
      </CompanyInfo>
    </>
  );
};
